import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "./Modal";
import { fetchWithAuth } from "../../utils/api";

const serverUrl = process.env.REACT_APP_SERVER_URL;

export default function DataReplicationWidget() {
  const navigate = useNavigate();
  const [isAllowed, setIsAllowed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sourceTestId, setSourceTestId] = useState("");
  const [answerPaperIdsText, setAnswerPaperIdsText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  // Check access on mount
  useEffect(() => {
    let isMounted = true;
    const checkAccess = async () => {
      try {
        const response = await fetchWithAuth(`${serverUrl}/api/replication/check-access`);
        if (!response || !response.ok) return;
        const data = await response.json();
        if (isMounted && data?.allowed) {
          setIsAllowed(true);
        }
      } catch (err) {
        // Silently ignore if not authorized
      }
    };

    checkAccess();
    return () => {
      isMounted = false;
    };
  }, []);

  if (!isAllowed) {
    return null;
  }

  const handleOpen = () => {
    setIsModalOpen(true);
    setError(null);
    setResult(null);
  };

  const handleClose = () => {
    if (loading) return;
    setIsModalOpen(false);
    setError(null);
  };

  const handleCopy = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1800);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedTestId = sourceTestId.trim();

    if (!trimmedTestId || trimmedTestId.length !== 24) {
      setError("Please provide a valid 24-character MongoDB sourceTestId.");
      return;
    }

    let answerPaperIds = undefined;
    if (answerPaperIdsText.trim()) {
      const parsedIds = answerPaperIdsText
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean);

      const invalid = parsedIds.some((id) => id.length !== 24);
      if (invalid) {
        setError("One or more answer paper IDs are not valid 24-character IDs.");
        return;
      }
      if (parsedIds.length > 0) {
        answerPaperIds = parsedIds;
      }
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetchWithAuth(`${serverUrl}/api/replication/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sourceTestId: trimmedTestId,
          answerPaperIds,
        }),
      });

      if (!response) {
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || "Failed to replicate test data");
      }

      setResult(data);
    } catch (err) {
      setError(err.message || "An unexpected error occurred during replication.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoToTest = (testId) => {
    setIsModalOpen(false);
    navigate(`/instructor/test-grading/${testId}`);
  };

  return (
    <>
      {/* GradeMe-Themed Floating Action Button at Bottom-Left */}
      <button
        type="button"
        onClick={handleOpen}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title="Replicate test data from PROD into this environment"
        style={{
          position: "fixed",
          bottom: "18px",
          left: "18px",
          zIndex: 9990,
          background: "#ffffff",
          color: "#1e293b",
          border: isHovered ? "1.5px solid var(--color-3, #8dc63f)" : "1.5px solid #e2e8f0",
          borderRadius: "100px",
          padding: "7px 14px 7px 9px",
          fontSize: "12px",
          fontWeight: 600,
          fontFamily: "var(--body-font, 'Montserrat', sans-serif)",
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer",
          boxShadow: isHovered
            ? "0 8px 24px rgba(141, 198, 63, 0.28), 0 2px 6px rgba(0, 0, 0, 0.08)"
            : "0 4px 16px rgba(0, 0, 0, 0.08)",
          transform: isHovered ? "translateY(-2px)" : "translateY(0)",
          transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <span
          style={{
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, var(--color-3, #8dc63f) 0%, #6da92a 100%)",
            color: "#ffffff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "11px",
            boxShadow: "0 2px 6px rgba(141, 198, 63, 0.4)",
          }}
        >
          <i className="fa-solid fa-arrows-rotate"></i>
        </span>
        <span style={{ letterSpacing: "-0.01em" }}>Replicate Test</span>
        <span
          style={{
            background: "rgba(141, 198, 63, 0.15)",
            color: "var(--color-6, #4e6b4b)",
            fontSize: "10px",
            fontWeight: 700,
            padding: "2px 6px",
            borderRadius: "100px",
            border: "1px solid rgba(141, 198, 63, 0.35)",
            letterSpacing: "0.03em",
          }}
        >
          PROD ➔ Your Institute
        </span>
      </button>

      {/* Modal Dialog */}
      <Modal isOpen={isModalOpen} onClose={handleClose} maxWidth="560px" closeButton={!loading}>
        <div style={{ fontFamily: "var(--body-font, 'Montserrat', sans-serif)", color: "#1e293b" }}>
          
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <div
              style={{
                width: "42px",
                height: "42px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, var(--color-3, #8dc63f) 0%, #6da92a 100%)",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
                flexShrink: 0,
                boxShadow: "0 4px 14px rgba(141, 198, 63, 0.35)",
              }}
            >
              <i className="fa-solid fa-clone"></i>
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: 700,
                    color: "#0f172a",
                    fontFamily: "var(--heading-font, 'Montserrat bold', sans-serif)",
                  }}
                >
                  Replicate Test Data
                </h3>
                <span
                  style={{
                    background: "rgba(141, 198, 63, 0.14)",
                    color: "var(--color-6, #4e6b4b)",
                    fontSize: "11px",
                    fontWeight: 700,
                    padding: "2px 8px",
                    borderRadius: "100px",
                    border: "1px solid rgba(141, 198, 63, 0.35)",
                  }}
                >
                  PROD ➔ Your Institute
                </span>
              </div>
              <p style={{ margin: "3px 0 0", fontSize: "12.5px", color: "#64748b" }}>
                Clone test, rubric, and answer papers from PROD into your institute.
              </p>
            </div>
          </div>

          <div style={{ height: "1px", background: "#f1f5f9", margin: "14px 0 18px" }} />

          {/* Result view */}
          {result ? (
            <div style={{ textAlign: "center", padding: "6px 0" }}>
              <div
                style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, var(--color-3, #8dc63f) 0%, #7bb83a 100%)",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  margin: "0 auto 12px",
                  boxShadow: "0 6px 20px rgba(141, 198, 63, 0.35)",
                }}
              >
                <i className="fa-solid fa-check"></i>
              </div>
              <h4
                style={{
                  margin: "0 0 6px",
                  fontSize: "17px",
                  fontWeight: 700,
                  color: "#0f172a",
                  fontFamily: "var(--heading-font, 'Montserrat bold', sans-serif)",
                }}
              >
                Replication Complete!
              </h4>
              <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#64748b" }}>
                Test and papers have been cloned as <strong style={{ color: "#0f172a" }}>Pending</strong> into your institute.
              </p>

              {/* Stats Card */}
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "14px",
                  padding: "16px",
                  textAlign: "left",
                  fontSize: "12.5px",
                  lineHeight: "1.7",
                  color: "#334155",
                  marginBottom: "22px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingBottom: "8px",
                    borderBottom: "1px solid #edf2f7",
                    marginBottom: "8px",
                  }}
                >
                  <span style={{ color: "#64748b" }}>Target Test ID:</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    <code
                      style={{
                        background: "#ffffff",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        padding: "2px 7px",
                        fontSize: "12px",
                        fontFamily: "monospace",
                        color: "#0f172a",
                      }}
                    >
                      {result.target?.testId}
                    </code>
                    <button
                      type="button"
                      onClick={() => handleCopy(result.target?.testId, "testId")}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        color: copiedKey === "testId" ? "var(--color-3, #8dc63f)" : "#94a3b8",
                        padding: "2px 4px",
                        fontSize: "12px",
                      }}
                      title="Copy Test ID"
                    >
                      <i className={copiedKey === "testId" ? "fa-solid fa-check" : "fa-regular fa-copy"}></i>
                    </button>
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingBottom: "8px",
                    borderBottom: "1px solid #edf2f7",
                    marginBottom: "8px",
                  }}
                >
                  <span style={{ color: "#64748b" }}>Target Class ID:</span>
                  <code
                    style={{
                      background: "#ffffff",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      padding: "2px 7px",
                      fontSize: "12px",
                      fontFamily: "monospace",
                      color: "#0f172a",
                    }}
                  >
                    {result.target?.classId}
                  </code>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "10px" }}>
                  <div
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "10px",
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "8px",
                        background: "rgba(141, 198, 63, 0.12)",
                        color: "var(--color-3, #8dc63f)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "13px",
                      }}
                    >
                      <i className="fa-solid fa-user-graduate"></i>
                    </div>
                    <div>
                      <div style={{ fontSize: "11px", color: "#64748b" }}>Students Cloned</div>
                      <div style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>
                        {result.counts?.students ?? 0}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "10px",
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "8px",
                        background: "rgba(59, 130, 246, 0.12)",
                        color: "#2563eb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "13px",
                      }}
                    >
                      <i className="fa-solid fa-file-lines"></i>
                    </div>
                    <div>
                      <div style={{ fontSize: "11px", color: "#64748b" }}>Answer Papers</div>
                      <div style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>
                        {result.counts?.answerPapers ?? 0}
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: "10px",
                    padding: "8px 12px",
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    fontSize: "11.5px",
                    color: "#475569",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <i className="fa-solid fa-cloud-arrow-up" style={{ color: "var(--color-3, #8dc63f)" }}></i>
                  <span>
                    Storage Files Copied: <strong>{result.files?.copied ?? 0}</strong>
                    {result.files?.skipped ? ` · Skipped: ${result.files.skipped}` : ""}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={handleClose}
                  style={{
                    padding: "9px 18px",
                    borderRadius: "100px",
                    border: "1.5px solid #e2e8f0",
                    background: "#ffffff",
                    color: "#475569",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => handleGoToTest(result.target?.testId)}
                  style={{
                    padding: "9px 22px",
                    borderRadius: "100px",
                    border: "none",
                    background: "var(--color-3, #8dc63f)",
                    color: "#ffffff",
                    fontSize: "13px",
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 4px 14px rgba(141, 198, 63, 0.35)",
                    transition: "all 0.2s ease",
                  }}
                >
                  <span>Go to Cloned Test</span>
                  <i className="fa-solid fa-arrow-right"></i>
                </button>
              </div>
            </div>
          ) : (
            /* Form view */
            <form onSubmit={handleSubmit}>
              {error && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    color: "#b91c1c",
                    padding: "10px 14px",
                    borderRadius: "10px",
                    fontSize: "12.5px",
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                  }}
                >
                  <i className="fa-solid fa-circle-exclamation" style={{ marginTop: "2px", color: "#dc2626" }}></i>
                  <span style={{ lineHeight: "1.5" }}>{error}</span>
                </div>
              )}

              {/* Source Test ID Input */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  htmlFor="sourceTestId"
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#334155",
                    marginBottom: "6px",
                  }}
                >
                  Source Test ID <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  id="sourceTestId"
                  type="text"
                  placeholder="e.g. 660f8d1234567890abcdef12"
                  value={sourceTestId}
                  onChange={(e) => setSourceTestId(e.target.value)}
                  disabled={loading}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    fontSize: "13px",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: "10px",
                    boxSizing: "border-box",
                    outline: "none",
                    fontFamily: "monospace",
                    background: "#f8fafc",
                    color: "#0f172a",
                    transition: "all 0.2s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "var(--color-3, #8dc63f)";
                    e.target.style.background = "#ffffff";
                    e.target.style.boxShadow = "0 0 0 3px rgba(141, 198, 63, 0.2)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.background = "#f8fafc";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              {/* Answer Paper IDs Textarea */}
              <div style={{ marginBottom: "18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                  <label
                    htmlFor="answerPaperIds"
                    style={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#334155",
                    }}
                  >
                    Answer Paper IDs
                  </label>
                  <span style={{ fontSize: "11px", fontWeight: 500, color: "#94a3b8" }}>Optional</span>
                </div>
                <textarea
                  id="answerPaperIds"
                  rows={3}
                  placeholder="Leave empty to clone all papers, or enter comma/line-separated 24-char IDs"
                  value={answerPaperIdsText}
                  onChange={(e) => setAnswerPaperIdsText(e.target.value)}
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    fontSize: "12px",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: "10px",
                    boxSizing: "border-box",
                    outline: "none",
                    fontFamily: "monospace",
                    background: "#f8fafc",
                    color: "#0f172a",
                    resize: "vertical",
                    transition: "all 0.2s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "var(--color-3, #8dc63f)";
                    e.target.style.background = "#ffffff";
                    e.target.style.boxShadow = "0 0 0 3px rgba(141, 198, 63, 0.2)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.background = "#f8fafc";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "5px", fontSize: "11.5px", color: "#64748b" }}>
                  <i className="fa-solid fa-circle-info" style={{ color: "var(--color-3, #8dc63f)", fontSize: "12px" }}></i>
                  <span>If left blank, all papers belonging to the test are cloned as &apos;Pending&apos;.</span>
                </div>
              </div>

              {/* Progress/Loading State */}
              {loading && (
                <div
                  style={{
                    background: "rgba(141, 198, 63, 0.08)",
                    border: "1.5px solid rgba(141, 198, 63, 0.35)",
                    color: "var(--color-6, #4e6b4b)",
                    padding: "12px 14px",
                    borderRadius: "10px",
                    fontSize: "12.5px",
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <i className="fa-solid fa-circle-notch fa-spin" style={{ color: "var(--color-3, #8dc63f)", fontSize: "16px" }}></i>
                  <div style={{ lineHeight: "1.4" }}>
                    <div style={{ fontWeight: 600 }}>Cloning Test &amp; Uploading Files...</div>
                    <div style={{ fontSize: "11px", color: "#64748b" }}>
                      Downloading from PROD storage and uploading to your institute. Please wait (typically 5–15s).
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "8px" }}>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  style={{
                    padding: "9px 20px",
                    borderRadius: "100px",
                    border: "1.5px solid #e2e8f0",
                    background: "#ffffff",
                    color: "#475569",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: loading ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !sourceTestId.trim()}
                  style={{
                    padding: "9px 24px",
                    borderRadius: "100px",
                    border: "none",
                    background:
                      loading || !sourceTestId.trim()
                        ? "#cbd5e1"
                        : "var(--color-3, #8dc63f)",
                    color: "#ffffff",
                    fontSize: "13px",
                    fontWeight: 700,
                    cursor: loading || !sourceTestId.trim() ? "not-allowed" : "pointer",
                    boxShadow:
                      loading || !sourceTestId.trim()
                        ? "none"
                        : "0 4px 14px rgba(141, 198, 63, 0.35)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.2s ease",
                  }}
                >
                  {loading ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin"></i>
                      <span>Replicating...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-clone"></i>
                      <span>Clone Test</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </Modal>
    </>
  );
}
